import React from 'react';

/**
 * React Error Boundary that prevents a blank-screen crash by catching
 * render-time exceptions and showing the actual error message.
 * This is critical because users hitting a white screen have no way to
 * tell us what went wrong — this component surfaces the stack trace so
 * the engineer can fix the root cause immediately.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Surface to browser console so support can copy-paste
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] Render crash captured:', error, errorInfo);

    // Stale-bundle self-heal: after a deploy, an open tab may reference JS
    // chunks that no longer exist. Auto hard-reload ONCE to pick up the new
    // build instead of stranding the user on an error screen.
    const msg = `${error?.message || ''} ${error?.name || ''}`;
    const isChunkError = /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(msg);
    if (isChunkError && !sessionStorage.getItem('ft_chunk_reloaded')) {
      sessionStorage.setItem('ft_chunk_reloaded', '1');
      window.location.reload();
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    // Force a hard reload from the server (bypasses bf-cache)
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errorMsg = this.state.error?.message || String(this.state.error);
    const stack = this.state.errorInfo?.componentStack || this.state.error?.stack || '';

    return (
      <div
        className="min-h-[400px] flex items-center justify-center p-6"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-2xl w-full bg-white border border-red-200 rounded-lg shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xl font-bold">!</div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Something went wrong on this page</h2>
              <p className="text-sm text-slate-600 mt-1">
                Don&apos;t worry — your work has been preserved. Try the actions below, and if it
                keeps happening please send the technical detail to the office team.
              </p>
            </div>
          </div>

          <div className="bg-red-50 border border-red-100 rounded p-3 mb-4 text-sm text-red-800 font-mono break-words">
            {errorMsg}
          </div>

          {stack && (
            <details className="mb-4 text-xs">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                Technical detail (click to copy and send to support)
              </summary>
              <pre className="mt-2 p-2 bg-slate-100 rounded text-slate-700 overflow-auto max-h-48 whitespace-pre-wrap">{stack}</pre>
            </details>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={this.handleReset}
              className="px-4 py-2 text-sm font-semibold rounded bg-slate-100 hover:bg-slate-200 text-slate-800"
              data-testid="error-boundary-dismiss"
            >
              Dismiss & continue
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 text-sm font-semibold rounded bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="error-boundary-reload"
            >
              Reload the page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
